import { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownToLine,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileJson2,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  RotateCcw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  X,
} from "lucide-react";
import { api } from "./lib/api";
import { supabase } from "./lib/supabase";
import { takePendingFiles } from "./lib/pendingFiles";
import { GradientBackground } from "./components/GradientBackground";
import { FlowButton } from "./components/FlowButton";
import { AdSlot } from "./components/AdSlot";
import {
  BlogArticle,
  BlogIndex,
  LandingV2,
  PolicyPage,
} from "./components/PublicSite";
import { AdminCMS } from "./components/AdminCMS";
import {
  TurnstileWidget,
  type TurnstileHandle,
} from "./components/TurnstileWidget";

type DatasetStatus =
  | "CREATED"
  | "UPLOADING"
  | "UPLOADED"
  | "ANALYZING"
  | "FIELD_SELECTION_REQUIRED"
  | "READY_TO_PROCESS"
  | "PROCESSING"
  | "REVIEW_REQUIRED"
  | "COMPLETED"
  | "PARTIAL_FAILURE"
  | "FAILED"
  | "ARCHIVED";
type Dataset = {
  id: string;
  name: string;
  status: DatasetStatus;
  document_count: number;
  completed_count: number;
  row_count: number;
  review_count: number;
  failed_count: number;
  updated_at: string;
};
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const demoDatasets: Dataset[] = [
  {
    id: "march-expenses",
    name: "March Expenses",
    status: "REVIEW_REQUIRED",
    document_count: 146,
    completed_count: 145,
    row_count: 1248,
    review_count: 17,
    failed_count: 1,
    updated_at: "Today, 2:14 PM",
  },
  {
    id: "q1-statements",
    name: "Q1 Bank Statements",
    status: "COMPLETED",
    document_count: 21,
    completed_count: 21,
    row_count: 2874,
    review_count: 0,
    failed_count: 0,
    updated_at: "Aug 29, 2026",
  },
  {
    id: "feb-invoices",
    name: "February Invoices",
    status: "PROCESSING",
    document_count: 300,
    completed_count: 192,
    row_count: 2438,
    review_count: 8,
    failed_count: 1,
    updated_at: "Processing now",
  },
];

function Brand() {
  return (
    <Link to="/" className="brand">
      <span className="brand-mark">
        <img src="/bulk-invoice-data-extractor-logo.png" alt="" />
      </span>
      <span>Bulk Invoice Data Extractor</span>
    </Link>
  );
}

function PublicHeader() {
  return (
    <header className="public-header">
      <Brand />
      <nav aria-label="Public navigation">
        <Link to="/how-it-works">How it works</Link>
        <Link to="/privacy">Privacy</Link>
        <Link className="nav-cta" to="/login">
          Sign in
        </Link>
        <Link className="nav-cta nav-cta-green" to="/signup">
          Sign up
        </Link>
      </nav>
    </header>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Landing() {
  return (
    <main className="landing">
      <PublicHeader />
      <section className="hero">
        <GradientBackground className="hero-bg" />
        <div className="hero-content">
          <span className="eyebrow">
            <Sparkles size={14} /> Free to use
          </span>
          <h1>Turn hundreds of financial PDFs into one clean spreadsheet.</h1>
          <p>
            Upload invoices, receipts and bank statements. Choose the fields you
            need, review anything uncertain, then export everything to Excel,
            CSV, Google Sheets or JSON.
          </p>
          <div className="hero-actions">
            <Link to="/signup">
              <FlowButton text="Parse PDFs" />
            </Link>
            <a className="text-link" href="#how">
              See how it works <ChevronRight size={16} />
            </a>
          </div>
          <p className="trust-line">
            <ShieldCheck size={16} /> Private files · Local-first extraction ·
            No card required
          </p>
        </div>
      </section>
      <section id="how" className="section">
        <div className="section-heading">
          <span className="kicker">A calmer way through paperwork</span>
          <h2>From a folder of PDFs to usable data</h2>
        </div>
        <div className="steps">
          {[
            ["01", "Upload", "Drop in up to 300 PDFs."],
            [
              "02",
              "Pick your fields",
              "We inspect your files and suggest useful fields automatically.",
            ],
            ["03", "Review", "Anything uncertain is clearly flagged."],
            [
              "04",
              "Export",
              "Get one clean CSV, Excel file, Google Sheet or JSON export.",
            ],
          ].map(([n, t, d]) => (
            <article key={n}>
              <span>{n}</span>
              <h3>{t}</h3>
              <p>{d}</p>
            </article>
          ))}
        </div>
      </section>
      <AdSlot placement="LANDING_MIDDLE" />
      <section className="types section">
        <div>
          <span className="kicker">Built for real financial documents</span>
          <h2>
            Invoices, receipts and statements—even when the layout changes.
          </h2>
          <p>
            Bulk Invoice Data Extractor starts with selectable text and local
            OCR. GLM-5.3-Flash is used only when meaning or visual structure
            cannot be recovered reliably.
          </p>
        </div>
        <div className="type-list">
          {[
            ["Invoices", "Supplier, dates, totals and line items"],
            ["Receipts", "Merchant, tax, payment method and purchases"],
            ["Bank statements", "Balances and hundreds of transaction rows"],
          ].map(([t, d]) => (
            <div key={t}>
              <FileText />
              <span>
                <strong>{t}</strong>
                <small>{d}</small>
              </span>
              <CheckCircle2 />
            </div>
          ))}
        </div>
      </section>
      <section className="final-cta">
        <GradientBackground className="hero-bg" />
        <div>
          <h2>Your next spreadsheet starts with a drop zone.</h2>
          <p>Keep the PDFs. Lose the manual copy-and-paste.</p>
          <Link to="/signup">
            <FlowButton text="Start a Dataset" />
          </Link>
        </div>
      </section>
      <footer>
        <Brand />
        <p>
          Extracted information should be reviewed before being used for
          accounting or tax filing.
        </p>
        <div>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </main>
  );
}

function AuthPage({ mode }: { mode: "login" | "signup" | "forgot" | "reset" }) {
  const navigate = useNavigate();
  const authLocation = useLocation();
  const requestedReturn = new URLSearchParams(authLocation.search).get(
    "returnTo",
  );
  const returnTo =
    requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
      ? requestedReturn
      : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const titles = {
    login: ["Welcome back", "Sign in to continue to your Datasets."],
    signup: [
      "Create your account",
      "Start turning PDFs into clean, reviewable data.",
    ],
    forgot: [
      "Reset your password",
      "We’ll send a secure recovery link to your email.",
    ],
    reset: ["Choose a new password", "Use at least eight characters."],
  };
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (!supabase)
        throw new Error(
          "Add the Supabase browser settings to .env.local to enable authentication.",
        );
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        navigate(returnTo);
      } else if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${location.origin}/login` },
        });
        if (error) throw error;
        setMessage("Check your email to verify your account.");
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${location.origin}/reset-password`,
        });
        if (error) throw error;
        setMessage("If an account exists, a recovery email is on its way.");
      } else {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        setMessage("Password updated. You can continue to your account.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed.");
    } finally {
      setLoading(false);
    }
  }
  return (
    <main className="auth-page">
      <GradientBackground className="auth-bg" />
      <Link className="auth-brand" to="/">
        <Brand />
      </Link>
      <section className="auth-card">
        <span className="eyebrow">Secure workspace</span>
        <h1>{titles[mode][0]}</h1>
        <p>{titles[mode][1]}</p>
        <form onSubmit={submit}>
          {mode !== "reset" && (
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
          )}
          {mode !== "forgot" && (
            <label>
              {mode === "reset" ? "New password" : "Password"}
              <input
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={
                  mode === "login" ? "current-password" : "new-password"
                }
              />
            </label>
          )}
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          {message && (
            <div className="form-success" role="status">
              {message}
            </div>
          )}
          <FlowButton
            text={
              mode === "login"
                ? "Sign in"
                : mode === "signup"
                  ? "Create account"
                  : mode === "forgot"
                    ? "Send recovery link"
                    : "Update password"
            }
            loading={loading}
            type="submit"
          />
        </form>
        {mode === "login" && (
          <>
            <Link className="minor-link" to="/forgot-password">
              Forgot password?
            </Link>
            <p className="auth-switch">
              New here? <Link to="/signup">Create account</Link>
            </p>
          </>
        )}
        {mode === "signup" && (
          <p className="auth-switch">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        )}
      </section>
    </main>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState({
    email: "Account",
    is_admin: DEMO_MODE,
  });
  const navigate = useNavigate();
  const nav = [
    ["/app", Home, "Home"],
    ["/app/folders", FolderOpen, "Folders"],
    ["/app/recent", Clock3, "Recent Datasets"],
    ["/app/exports", ArrowDownToLine, "Exports"],
    ["/app/settings", Settings, "Settings"],
  ];
  useEffect(() => {
    if (DEMO_MODE) {
      setAccount({ email: "demo@example.com", is_admin: true });
      return;
    }
    api<{ email: string; is_admin: boolean }>("/v1/me")
      .then(setAccount)
      .catch(() => {});
  }, []);
  async function logout() {
    await supabase?.auth.signOut();
    navigate("/");
  }
  return (
    <div className="app-shell">
      <button
        className="mobile-menu"
        onClick={() => setOpen(!open)}
        aria-label="Toggle navigation"
      >
        <Menu />
      </button>
      <aside className={open ? "sidebar open" : "sidebar"}>
        <Brand />
        <nav>
          {nav.map(([to, Icon, label]) => (
            <NavLink
              key={to as string}
              to={to as string}
              end={to === "/app"}
              onClick={() => setOpen(false)}
            >
              {<Icon size={18} />}
              <span>{label as string}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <button onClick={logout}>
            <span className="avatar">
              {account.email.slice(0, 2).toUpperCase()}
            </span>
            <span>
              {account.email}
              <small>Log out</small>
            </span>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <div className="app-main">{children}</div>
    </div>
  );
}

function ProtectedApp({ children }: { children: React.ReactNode }) {
  const protectedLocation = useLocation();
  const [signedIn, setSignedIn] = useState<boolean | null>(
    DEMO_MODE ? true : null,
  );
  useEffect(() => {
    if (DEMO_MODE) return;
    if (!supabase) {
      setSignedIn(false);
      return;
    }
    supabase.auth
      .getSession()
      .then(({ data }) => setSignedIn(Boolean(data.session)));
    const { data } = supabase.auth.onAuthStateChange((_event, session) =>
      setSignedIn(Boolean(session)),
    );
    return () => data.subscription.unsubscribe();
  }, []);
  if (signedIn === null)
    return (
      <main className="auth-page">
        <p>Checking your session…</p>
      </main>
    );
  if (!signedIn)
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(protectedLocation.pathname + protectedLocation.search)}`}
        replace
      />
    );
  return children;
}

function StatusPill({ status }: { status: DatasetStatus }) {
  const label = status.replaceAll("_", " ").toLowerCase();
  return (
    <span className={`status status-${status.toLowerCase()}`}>
      {status === "REVIEW_REQUIRED" ? (
        <AlertTriangle />
      ) : status === "COMPLETED" ? (
        <CheckCircle2 />
      ) : (
        <Clock3 />
      )}
      {label}
    </span>
  );
}

function useDatasets() {
  const [datasets, setDatasets] = useState<Dataset[]>(
    DEMO_MODE ? demoDatasets : [],
  );
  useEffect(() => {
    if (DEMO_MODE || !supabase) return;
    supabase
      .from("datasets")
      .select(
        "id,name,status,document_count,completed_count,row_count,review_count,failed_count,updated_at",
      )
      .order("updated_at", { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (data) setDatasets(data as Dataset[]);
      });
  }, []);
  return datasets;
}
function Dashboard() {
  const datasets = useDatasets();
  const totals = datasets.reduce(
    (a, d) => ({
      docs: a.docs + d.document_count,
      rows: a.rows + d.row_count,
      review: a.review + d.review_count,
    }),
    { docs: 0, rows: 0, review: 0 },
  );
  return (
    <Page>
      <header className="page-header">
        <div>
          <span className="kicker">Document workspace</span>
          <h1>Good evening.</h1>
          <p>
            {totals.review
              ? `${totals.review} values are waiting for review.`
              : "Your uploaded Datasets will appear here."}
          </p>
        </div>
        <Link to="/app/datasets/new">
          <FlowButton text="New Dataset" />
        </Link>
      </header>
      <section className="metric-row">
        {[
          ["PDFs processed", String(totals.docs), "Across your Datasets"],
          [
            "Rows extracted",
            totals.rows.toLocaleString(),
            "All document types",
          ],
          ["Need review", String(totals.review), "Uncertain values"],
          ["Recent Datasets", String(datasets.length), "Most recently updated"],
        ].map(([t, v, s]) => (
          <article key={t}>
            <small>{t}</small>
            <strong>{v}</strong>
            <span>{s}</span>
          </article>
        ))}
      </section>
      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-title">
            <div>
              <h2>Recent Datasets</h2>
              <p>Live status from background processing</p>
            </div>
            <Link to="/app/recent">View all</Link>
          </div>
          <DatasetList datasets={datasets} />
        </section>
        <aside>
          <AdSlot placement="DASHBOARD_SIDEBAR" />
          <section className="panel folder-panel">
            <div className="panel-title">
              <h2>Folders</h2>
              <Link to="/app/folders">View all</Link>
            </div>
            <div className="folder-row">
              <Folder />
              <span>
                <strong>Organize your batches</strong>
                <small>Folders keep separate Datasets together</small>
              </span>
              <ChevronRight />
            </div>
          </section>
        </aside>
      </div>
    </Page>
  );
}

function DatasetList({ datasets }: { datasets: Dataset[] }) {
  return (
    <div className="dataset-list">
      {datasets.length ? (
        datasets.map((d) => (
          <Link to={`/app/datasets/${d.id}`} key={d.id}>
            <div className="file-icon">
              <FileText />
            </div>
            <span className="dataset-name">
              <strong>{d.name}</strong>
              <small>
                {d.document_count} PDFs · {d.row_count.toLocaleString()} rows ·{" "}
                {new Date(d.updated_at).toLocaleString()}
              </small>
            </span>
            <StatusPill status={d.status} />
            <ChevronRight />
          </Link>
        ))
      ) : (
        <div className="folder-row">
          <span>
            <strong>No Datasets yet</strong>
            <small>Create one to upload PDFs.</small>
          </span>
        </div>
      )}
    </div>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return <main className="page">{children}</main>;
}

function NewDataset() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function create() {
    setLoading(true);
    setError("");
    try {
      if (DEMO_MODE) {
        nav(`/app/datasets/${crypto.randomUUID()}/upload`);
        return;
      }
      if (!supabase) throw new Error("Supabase is not configured.");
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error("Please sign in again.");
      const { data, error } = await supabase
        .from("datasets")
        .insert({ name, user_id: user.id, status: "CREATED" })
        .select("id")
        .single();
      if (error) throw error;
      nav(`/app/datasets/${data.id}/upload`);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not create the Dataset.",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Page>
      <header className="page-header compact">
        <div>
          <span className="kicker">New Dataset · Step 1 of 2</span>
          <h1>Name this batch</h1>
          <p>Each upload batch becomes its own Dataset.</p>
        </div>
      </header>
      <section className="form-panel">
        <label>
          Dataset name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="March Expenses"
          />
        </label>
        <label>
          Folder
          <select defaultValue="">
            <option value="">No folder</option>
          </select>
        </label>
        {error && <div className="form-error">{error}</div>}
        <FlowButton
          text="Continue"
          loading={loading}
          disabled={!name.trim()}
          onClick={create}
        />
      </section>
    </Page>
  );
}

type UploadItem = {
  id: string;
  file: File;
  progress: number;
  status: "waiting" | "uploading" | "complete" | "failed";
  error?: string;
};
function UploadPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [drag, setDrag] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const authorizationTailRef = useRef<Promise<void>>(Promise.resolve());
  function add(files: FileList | File[] | null) {
    if (!files) return;
    const pdfs = Array.from(files)
      .filter(
        (f) =>
          f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
      )
      .slice(0, 300 - items.length)
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: "waiting" as const,
      }));
    setItems((x) => [...x, ...pdfs]);
    void uploadBatch(pdfs);
  }
  useEffect(() => {
    let active = true;
    void takePendingFiles().then((files) => {
      if (active && files.length) add(files);
    });
    return () => {
      active = false;
    };
    // Pending files are consumed once when the authenticated upload route opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  async function uploadBatch(batch: UploadItem[]) {
    const queue = [...batch];
    const workers = Array.from(
      { length: Math.min(5, queue.length) },
      async () => {
        while (queue.length) {
          const item = queue.shift();
          if (item) await uploadOne(item);
        }
      },
    );
    await Promise.all(workers);
  }
  async function uploadOne(item: UploadItem) {
    try {
      setItems((xs) =>
        xs.map((x) => (x.id === item.id ? { ...x, status: "uploading" } : x)),
      );
      if (DEMO_MODE) {
        setItems((xs) =>
          xs.map((x) =>
            x.id === item.id ? { ...x, status: "complete", progress: 100 } : x,
          ),
        );
        return;
      }
      const auth = await authorizeUpload(item);
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", auth.upload_url);
        Object.entries(auth.required_headers).forEach(([k, v]) =>
          xhr.setRequestHeader(k, v),
        );
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable)
            setItems((xs) =>
              xs.map((x) =>
                x.id === item.id
                  ? { ...x, progress: Math.round((e.loaded / e.total) * 100) }
                  : x,
              ),
            );
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error("Direct upload failed"));
        xhr.onerror = () => reject(new Error("Direct upload failed"));
        xhr.send(item.file);
      });
      await api(`/v1/datasets/${id}/uploads/confirm`, {
        method: "POST",
        body: JSON.stringify({
          document_id: auth.document_id,
          object_key: auth.object_key,
          size_bytes: item.file.size,
        }),
      });
      setItems((xs) =>
        xs.map((x) =>
          x.id === item.id
            ? { ...x, status: "complete", progress: 100, error: undefined }
            : x,
        ),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "The upload failed.";
      setItems((xs) =>
        xs.map((x) =>
          x.id === item.id ? { ...x, status: "failed", error: message } : x,
        ),
      );
    }
  }
  async function authorizeUpload(item: UploadItem) {
    let release!: () => void;
    const previous = authorizationTailRef.current;
    authorizationTailRef.current = new Promise<void>((resolve) => {
      release = resolve;
    });
    await previous;
    try {
      if (!turnstileRef.current)
        throw new Error("The upload security check is not ready.");
      const turnstileToken = await turnstileRef.current.takeToken();
      return await api<{
        document_id: string;
        object_key: string;
        upload_url: string;
        required_headers: Record<string, string>;
      }>(`/v1/datasets/${id}/uploads/authorize`, {
        method: "POST",
        body: JSON.stringify({
          filename: item.file.name,
          content_type: item.file.type || "application/pdf",
          size_bytes: item.file.size,
          turnstile_token: turnstileToken,
        }),
      });
    } finally {
      turnstileRef.current?.reset();
      release();
    }
  }
  const done = items.length > 0 && items.every((x) => x.status === "complete");
  async function analyze() {
    setAnalyzing(true);
    try {
      if (!DEMO_MODE)
        await api(`/v1/datasets/${id}/analyze`, { method: "POST", body: "{}" });
      nav(`/app/datasets/${id}/fields`);
    } finally {
      setAnalyzing(false);
    }
  }
  return (
    <Page>
      <header className="page-header compact">
        <div>
          <span className="kicker">New Dataset · Step 2 of 2</span>
          <h1>Add your PDFs</h1>
          <p>
            Files upload directly to private storage. Up to 300 files per
            Dataset.
          </p>
        </div>
      </header>
      {!DEMO_MODE && (
        <section className="turnstile-panel" aria-label="Upload security check">
          <div>
            <ShieldCheck />
            <span>
              <strong>Protected upload</strong>
              <small>
                A fresh Cloudflare check securely authorizes each PDF.
              </small>
            </span>
          </div>
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
          />
        </section>
      )}
      <section
        className={`drop-zone ${drag ? "dragging" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          add(e.dataTransfer.files);
        }}
      >
        <GradientBackground className="drop-bg" />
        <div>
          <span className="upload-mark">
            <UploadCloud />
          </span>
          <h2>Drop your PDFs here</h2>
          <p>Invoices, receipts and bank statements · 50 MB per file</p>
          <label className="file-picker">
            Choose files
            <input
              type="file"
              multiple
              accept="application/pdf,.pdf"
              onChange={(e) => add(e.target.files)}
            />
          </label>
        </div>
      </section>
      {items.length > 0 && (
        <section className="upload-list">
          <div className="upload-summary">
            <strong>{items.length} files</strong>
            <span>
              {items.filter((x) => x.status === "complete").length} uploaded
            </span>
          </div>
          {items.map((x) => (
            <div key={x.id}>
              <FileText />
              <span>
                <strong>{x.file.name}</strong>
                <small>{(x.file.size / 1024 / 1024).toFixed(1)} MB</small>
                {x.error && <small className="upload-error">{x.error}</small>}
                <i>
                  <b style={{ width: `${x.progress}%` }} />
                </i>
              </span>
              <em>
                {x.status === "complete" ? (
                  <Check />
                ) : x.status === "failed" ? (
                  "Failed"
                ) : (
                  `${x.progress}%`
                )}
              </em>
              {x.status === "failed" && (
                <button
                  className="retry-upload"
                  onClick={() => {
                    setItems((xs) =>
                      xs.map((item) =>
                        item.id === x.id
                          ? {
                              ...item,
                              status: "waiting",
                              progress: 0,
                              error: undefined,
                            }
                          : item,
                      ),
                    );
                    void uploadOne(x);
                  }}
                >
                  Retry
                </button>
              )}
              <button
                onClick={() =>
                  setItems((xs) => xs.filter((y) => y.id !== x.id))
                }
                aria-label={`Remove ${x.file.name}`}
              >
                <X />
              </button>
            </div>
          ))}
        </section>
      )}
      <div className="sticky-actions">
        <span>
          {done
            ? "All files uploaded. Ready to inspect."
            : "Uploads can recover individually if your connection changes."}
        </span>
        <FlowButton
          text="Analyze files"
          loading={analyzing}
          disabled={!done}
          onClick={analyze}
        />
      </div>
    </Page>
  );
}

const recommended = [
  ["supplier", "Supplier", "Text", true],
  ["invoice_number", "Invoice Number", "Text", true],
  ["invoice_date", "Invoice Date", "Date", true],
  ["currency", "Currency", "Currency", true],
  ["subtotal", "Subtotal", "Number", true],
  ["tax", "Tax", "Number", true],
  ["total", "Total", "Currency", true],
  ["due_date", "Due Date", "Date", false],
] as const;
const repeating = [
  ["description", "Description", "Text", true],
  ["quantity", "Quantity", "Number", true],
  ["unit_price", "Unit Price", "Currency", true],
  ["line_total", "Line Total", "Currency", true],
] as const;
function FieldsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const defaults = [...recommended, ...repeating];
  const [available, setAvailable] = useState<
    readonly (readonly [string, string, string, boolean])[]
  >(DEMO_MODE ? defaults : []);
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set<string>(
        DEMO_MODE ? defaults.filter((x) => x[3]).map((x) => x[0]) : [],
      ),
  );
  const [custom, setCustom] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (DEMO_MODE || !supabase) return;
    let timer: number;
    async function load() {
      const { data } = await supabase!
        .from("dataset_fields")
        .select("schema")
        .eq("dataset_id", id)
        .maybeSingle();
      const schema = data?.schema as
        | { key: string; label: string; type: string; repeating: boolean }[]
        | undefined;
      if (schema) {
        const fields = schema.map(
          (x) => [x.key, x.label, x.type, x.repeating] as const,
        );
        setAvailable(fields);
        setSelected(new Set(fields.map((x) => x[0])));
      } else {
        timer = window.setTimeout(load, 2000);
      }
    }
    void load();
    return () => clearTimeout(timer);
  }, [id]);
  function toggle(k: string) {
    setSelected((s) => {
      const n = new Set<string>(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  }
  async function start() {
    setLoading(true);
    try {
      if (!DEMO_MODE) {
        const all = available
          .filter((x) => selected.has(x[0]))
          .map(([key, label, type, repeatingField]) => ({
            key,
            label,
            type: type.toUpperCase(),
            repeating: repeatingField,
          }));
        await api(`/v1/datasets/${id}/start-extraction`, {
          method: "POST",
          body: JSON.stringify({ fields: all }),
        });
      }
      nav(`/app/datasets/${id}/processing`);
    } finally {
      setLoading(false);
    }
  }
  const documentFields = available.filter((x) => !x[3]);
  const repeatingFields = available.filter((x) => x[3]);
  return (
    <Page>
      <header className="page-header compact">
        <div>
          <span className="kicker">
            {available.length
              ? "Analysis complete"
              : "Analyzing representative layouts"}
          </span>
          <h1>What would you like in your spreadsheet?</h1>
          <p>
            {available.length
              ? "Recommended fields are based on representative layouts from this Dataset."
              : "You can leave this page. Field recommendations will appear when analysis finishes."}
          </p>
        </div>
      </header>
      {DEMO_MODE && (
        <section className="classification-strip">
          <span>Development fixture</span>
          <div>
            <strong>128</strong> Invoices
          </div>
          <div>
            <strong>94</strong> Receipts
          </div>
          <div>
            <strong>21</strong> Bank statements
          </div>
        </section>
      )}
      <FieldGroup
        title="Document fields"
        note="One value per document"
        fields={documentFields}
        selected={selected}
        toggle={toggle}
      />
      <FieldGroup
        title="Repeating fields"
        note="One PDF may create many rows"
        fields={repeatingFields}
        selected={selected}
        toggle={toggle}
      />
      {custom.map((c, i) => (
        <div key={i} className="custom-field">
          <CheckCircle2 /> {c}
        </div>
      ))}
      <button
        className="add-field"
        onClick={() => {
          const n = prompt("Field name");
          if (n) setCustom((x) => [...x, n]);
        }}
      >
        <Plus /> Add field
      </button>
      <div className="sticky-actions">
        <span>{selected.size + custom.length} fields selected</span>
        <div>
          <button
            className="plain-button"
            onClick={() => setSelected(new Set<string>())}
          >
            Clear
          </button>
          <FlowButton
            text="Start extraction"
            loading={loading}
            disabled={selected.size === 0}
            onClick={start}
          />
        </div>
      </div>
    </Page>
  );
}
function FieldGroup({
  title,
  note,
  fields,
  selected,
  toggle,
}: {
  title: string;
  note: string;
  fields: readonly (readonly [string, string, string, boolean])[];
  selected: Set<string>;
  toggle: (x: string) => void;
}) {
  return (
    <section className="field-panel">
      <div>
        <h2>{title}</h2>
        <p>{note}</p>
      </div>
      <div>
        {fields.map(([key, label, type]) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={selected.has(key)}
              onChange={() => toggle(key)}
            />
            <span>
              <strong>{label}</strong>
              <small>{type}</small>
            </span>
            <Check className="checkmark" />
          </label>
        ))}
      </div>
    </section>
  );
}

function ProcessingPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState({
    name: "Dataset",
    document_count: DEMO_MODE ? 300 : 0,
    completed_count: DEMO_MODE ? 192 : 0,
    review_count: DEMO_MODE ? 17 : 0,
    failed_count: DEMO_MODE ? 1 : 0,
    status: "PROCESSING",
  });
  useEffect(() => {
    if (DEMO_MODE) return;
    let active = true;
    async function refresh() {
      try {
        const next = await api<typeof status>(`/v1/datasets/${id}/status`);
        if (active) setStatus(next);
      } catch {
        // A later polling cycle retries transient status failures.
      }
    }
    void refresh();
    const t = setInterval(refresh, 3000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [id]);
  const progress = status.document_count
    ? (status.completed_count / status.document_count) * 360
    : 0;
  const finished = [
    "COMPLETED",
    "REVIEW_REQUIRED",
    "PARTIAL_FAILURE",
    "FAILED",
  ].includes(status.status);
  return (
    <Page>
      <header className="page-header compact">
        <div>
          <span className="kicker">Background processing</span>
          <h1>Processing {status.name}</h1>
          <p>You can leave this page. Processing will continue.</p>
        </div>
      </header>
      <section className="processing-card">
        <GradientBackground className="processing-bg" />
        <div
          className="processing-ring"
          style={{ "--progress": `${progress}deg` } as React.CSSProperties}
        >
          <span>
            <strong>{status.completed_count}</strong>
            <small>of {status.document_count}</small>
          </span>
        </div>
        <div>
          <span className="live-dot">{finished ? "Finished" : "Live"}</span>
          <h2>
            {finished
              ? "Your Dataset is ready"
              : "Extracting fields and validating totals"}
          </h2>
          <p>
            Local extraction is tried first. Difficult layouts are routed to GLM
            only when needed.
          </p>
        </div>
      </section>
      <section className="processing-stats">
        {[
          [
            "Files complete",
            `${status.completed_count} / ${status.document_count}`,
          ],
          ["Need review", String(status.review_count)],
          ["Failed", String(status.failed_count)],
          ["Dataset status", status.status.replaceAll("_", " ")],
        ].map(([a, b]) => (
          <div key={a}>
            <span>{a}</span>
            <strong>{b}</strong>
          </div>
        ))}
      </section>
      <section className="activity">
        <h2>Current activity</h2>
        {[
          [
            "Reading documents",
            status.completed_count ? "In progress" : "Waiting",
            CheckCircle2,
          ],
          ["Running local OCR", "When scans require it", RotateCcw],
          [
            "Validating extracted values",
            `${status.completed_count} / ${status.document_count}`,
            Clock3,
          ],
          ["Preparing the Dataset", finished ? "Ready" : "Waiting", Clock3],
        ].map(([a, b, I]) => (
          <div key={a as string}>
            <I />
            <span>
              <strong>{a as string}</strong>
              <small>{b as string}</small>
            </span>
          </div>
        ))}
      </section>
      {finished && (
        <button
          className="demo-complete"
          onClick={() =>
            nav(
              status.review_count
                ? `/app/datasets/${id}/review`
                : `/app/datasets/${id}`,
            )
          }
        >
          {status.review_count ? "Review uncertain values" : "Open Dataset"}
        </button>
      )}
    </Page>
  );
}

type ReviewItem = {
  id: string;
  field_key: string;
  raw_value: string | null;
  review_reason: string | null;
  candidates: string[];
  source_page: number | null;
  extraction_method: string;
  source_url: string | null;
  documents: { filename: string };
};
const reviewRows: ReviewItem[] = [
  {
    id: "demo-1",
    documents: { filename: "invoice-0142.pdf" },
    field_key: "invoice_date",
    raw_value: "06-08-26",
    review_reason: "Date format is ambiguous",
    candidates: ["2026-08-06", "2026-06-08"],
    source_page: 1,
    extraction_method: "NATIVE_TEXT",
    source_url: null,
  },
];
function ReviewPage() {
  const { id } = useParams();
  const [rows, setRows] = useState<ReviewItem[]>(DEMO_MODE ? reviewRows : []);
  const [index, setIndex] = useState(0);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(!DEMO_MODE);
  useEffect(() => {
    if (DEMO_MODE) return;
    api<{ items: ReviewItem[] }>(`/v1/datasets/${id}/review`)
      .then((x) => setRows(x.items))
      .finally(() => setLoading(false));
  }, [id]);
  const row = rows[index];
  async function save() {
    if (!row) return;
    if (!DEMO_MODE)
      await api(`/v1/datasets/${id}/review/${row.id}`, {
        method: "PATCH",
        body: JSON.stringify({ corrected_value: value }),
      });
    setRows((current) => current.filter((x) => x.id !== row.id));
    setIndex(0);
    setValue("");
  }
  if (loading)
    return (
      <Page>
        <p>Loading review queue…</p>
      </Page>
    );
  if (!row)
    return (
      <Page>
        <section className="empty-state">
          <GradientBackground className="drop-bg" />
          <div>
            <CheckCircle2 />
            <h2>Review complete</h2>
            <p>No uncertain values remain in this Dataset.</p>
            <Link to={`/app/datasets/${id}`}>
              <FlowButton text="Open Dataset" />
            </Link>
          </div>
        </section>
      </Page>
    );
  return (
    <Page>
      <header className="page-header compact review-header">
        <div>
          <span className="kicker">Review queue</span>
          <h1>Review uncertain values</h1>
          <p>
            {rows.length} values remain. Corrections are saved with an audit
            trail.
          </p>
        </div>
        <span className="review-count">
          {index + 1} / {rows.length}
        </span>
      </header>
      <div className="review-layout">
        <section className="pdf-preview">
          <div className="pdf-toolbar">
            <span>{row.documents.filename}</span>
            <span>Page {row.source_page ?? "—"}</span>
          </div>
          {row.source_url ? (
            <iframe
              title={`Source ${row.documents.filename}`}
              src={row.source_url}
            />
          ) : (
            <div className="paper">
              <small>SOURCE EVIDENCE</small>
              <h3>PDF</h3>
              <dl>
                <dt>{row.field_key.replaceAll("_", " ")}</dt>
                <dd className="highlight">{row.raw_value}</dd>
              </dl>
              <div className="paper-lines" />
            </div>
          )}
        </section>
        <section className="correction">
          <span className="issue-label">
            <AlertTriangle /> Needs review
          </span>
          <h2>{row.field_key.replaceAll("_", " ")}</h2>
          <p className="reason">{row.review_reason}</p>
          <label>
            Source value
            <input readOnly value={row.raw_value ?? ""} />
          </label>
          <label>
            Corrected value
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter the confirmed value"
            />
          </label>
          {row.candidates.length > 0 && (
            <div className="candidates">
              <span>Possible values</span>
              {row.candidates.map((x) => (
                <button key={x} onClick={() => setValue(x)}>
                  {x}
                </button>
              ))}
            </div>
          )}
          <div className="evidence">
            <strong>Evidence</strong>
            <span>Page {row.source_page ?? "—"}</span>
            <small>Method: {row.extraction_method.replaceAll("_", " ")}</small>
          </div>
          <FlowButton text="Save and next" disabled={!value} onClick={save} />
          <button className="plain-button">Leave unresolved</button>
        </section>
      </div>
    </Page>
  );
}

function DatasetPage() {
  const { id } = useParams();
  const datasets = useDatasets();
  const dataset = datasets.find((x) => x.id === id);
  if (!dataset)
    return (
      <Page>
        <section className="empty-state">
          <GradientBackground className="drop-bg" />
          <div>
            <FileText />
            <h2>Dataset not available</h2>
            <p>It may still be loading, or you may not have access.</p>
          </div>
        </section>
      </Page>
    );
  return (
    <Page>
      <header className="page-header">
        <div>
          <span className="kicker">Dataset</span>
          <h1>{dataset.name}</h1>
          <p>
            {dataset.document_count} source PDFs · Updated{" "}
            {new Date(dataset.updated_at).toLocaleString()}
          </p>
        </div>
        <div className="header-actions">
          {dataset.review_count > 0 && (
            <Link to={`/app/datasets/${id}/review`} className="review-button">
              <AlertTriangle />
              Review {dataset.review_count} values
            </Link>
          )}
          <Link to="/app/exports" className="export-button">
            <ArrowDownToLine />
            Export
          </Link>
        </div>
      </header>
      <section className="dataset-summary">
        <StatusPill status={dataset.status} />
        {[
          ["Documents", dataset.document_count],
          ["Rows", dataset.row_count],
          ["Need review", dataset.review_count],
          ["Failed", dataset.failed_count],
        ].map(([x, y]) => (
          <div key={x}>
            <small>{x}</small>
            <strong>{Number(y).toLocaleString()}</strong>
          </div>
        ))}
      </section>
      <section className="result-panel">
        <div className="result-toolbar">
          <div className="search">
            <Search />
            <input placeholder="Search this Dataset" />
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Source file</th>
                <th>Type</th>
                <th>Supplier</th>
                <th>Date</th>
                <th>Description</th>
                <th className="numeric">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_MODE &&
                Array.from({ length: 12 }, (_, i) => (
                  <tr key={i}>
                    <td>
                      <FileText />
                      invoice-{String(i + 1).padStart(4, "0")}.pdf
                    </td>
                    <td>Invoice</td>
                    <td>
                      {
                        [
                          "Northstar Studio",
                          "Jade Supply Co.",
                          "Harbor Office",
                        ][i % 3]
                      }
                    </td>
                    <td>
                      {i === 5
                        ? "06-08-26"
                        : `2026-03-${String(i + 2).padStart(2, "0")}`}
                    </td>
                    <td>
                      {
                        [
                          "Design services",
                          "Office supplies",
                          "Software license",
                        ][i % 3]
                      }
                    </td>
                    <td className="numeric">
                      ${(128.4 + i * 42.85).toFixed(2)}
                    </td>
                    <td>
                      {i === 5 ? (
                        <span className="cell-review">
                          <AlertTriangle />
                          Review
                        </span>
                      ) : (
                        <span className="cell-ok">
                          <Check />
                          Confirmed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!DEMO_MODE && (
            <div className="folder-row">
              <span>
                <strong>No rows loaded in this view yet</strong>
                <small>Use Export after processing completes.</small>
              </span>
            </div>
          )}
        </div>
        <div className="pagination">
          <span>{dataset.row_count.toLocaleString()} rows</span>
        </div>
      </section>
    </Page>
  );
}

function Exports() {
  const datasets = useDatasets();
  const [datasetId, setDatasetId] = useState("");
  const [working, setWorking] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!datasetId && datasets[0]) setDatasetId(datasets[0].id);
  }, [datasets, datasetId]);
  async function create(format: string) {
    if (!datasetId) return;
    setWorking(format);
    setMessage("");
    try {
      if (DEMO_MODE) {
        setMessage(`${format} export queued in development mode.`);
        return;
      }
      const job = await api<{ id: string }>(
        `/v1/datasets/${datasetId}/exports`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ format }),
        },
      );
      setMessage(
        `Export ${job.id} is being generated. It will appear here when ready.`,
      );
    } catch (e) {
      if (format === "GOOGLE_SHEETS") {
        try {
          const result = await api<{ authorization_url: string }>(
            "/v1/google/connect",
          );
          location.assign(result.authorization_url);
          return;
        } catch {
          // The shared export error below gives the user a stable message.
        }
      }
      setMessage(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setWorking("");
    }
  }
  return (
    <Page>
      <header className="page-header">
        <div>
          <span className="kicker">Exports</span>
          <h1>Clean data, ready to use</h1>
          <p>
            Large files are generated in the background and expire after
            approximately one day.
          </p>
        </div>
      </header>
      <section className="form-panel">
        <label>
          Dataset
          <select
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
          >
            <option value="">Select a Dataset</option>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        {message && <div className="form-success">{message}</div>}
      </section>
      <section className="export-grid">
        {[
          [
            FileSpreadsheet,
            "Excel workbook",
            ".xlsx · Typed dates and currency",
            "XLSX",
          ],
          [FileText, "CSV file", ".csv · Universal and lightweight", "CSV"],
          [
            FileJson2,
            "Structured JSON",
            ".json · Preserves document hierarchy",
            "JSON",
          ],
          [
            LayoutDashboard,
            "Google Sheets",
            "Create a connected Sheet",
            "GOOGLE_SHEETS",
          ],
        ].map(([I, t, d, format]) => (
          <article key={t as string}>
            <I />
            <h2>{t as string}</h2>
            <p>{d as string}</p>
            <button
              disabled={!datasetId || working === format}
              onClick={() => create(format as string)}
            >
              {working === format ? "Working…" : "Export"} <ChevronRight />
            </button>
          </article>
        ))}
      </section>
    </Page>
  );
}

function Admin() {
  return (
    <Page>
      <header className="page-header">
        <div>
          <span className="kicker">Administration</span>
          <h1>Operations overview</h1>
          <p>
            Monitor fair use, job health and processing cost without viewing
            document contents.
          </p>
        </div>
        <span className="admin-badge">
          <ShieldCheck />
          Admin only
        </span>
      </header>
      <section className="metric-row">
        {[
          ["AI spend today", "$3.82", "$25.00 budget"],
          ["Cost / successful PDF", "$0.0041", "↓ 12% this week"],
          ["Local or OCR only", "81.6%", "Target ≥ 80%"],
          ["GLM vision", "4.2%", "63 of 1,500 PDFs"],
        ].map(([t, v, s]) => (
          <article key={t}>
            <small>{t}</small>
            <strong>{v}</strong>
            <span>{s}</span>
          </article>
        ))}
      </section>
      <div className="admin-grid">
        <section className="panel">
          <div className="panel-title">
            <h2>Processing controls</h2>
          </div>
          {[
            ["Processing enabled", true],
            ["AI processing enabled", true],
            ["New registrations", true],
          ].map(([x, on]) => (
            <label className="toggle-row" key={x as string}>
              <span>
                <strong>{x as string}</strong>
                <small>Changes apply to newly queued work.</small>
              </span>
              <input type="checkbox" defaultChecked={on as boolean} />
            </label>
          ))}
        </section>
        <section className="panel">
          <div className="panel-title">
            <h2>AI routing</h2>
          </div>
          {[
            ["Local only", "62.4%"],
            ["OCR only", "19.2%"],
            ["GLM text", "14.2%"],
            ["GLM vision", "4.2%"],
          ].map(([x, y]) => (
            <div className="bar-row" key={x}>
              <span>
                {x}
                <b>{y}</b>
              </span>
              <i>
                <em style={{ width: y }} />
              </i>
            </div>
          ))}
        </section>
      </div>
    </Page>
  );
}

function SimplePage({ title }: { title: string }) {
  const datasets = useDatasets();
  return (
    <Page>
      <header className="page-header">
        <div>
          <span className="kicker">Bulk Invoice Data Extractor</span>
          <h1>{title}</h1>
          <p>
            This production surface is connected through the application API.
          </p>
        </div>
      </header>
      {title === "Recent Datasets" ? (
        <section className="panel">
          <DatasetList datasets={datasets} />
        </section>
      ) : (
        <section className="empty-state">
          <GradientBackground className="drop-bg" />
          <div>
            <Folder />
            <h2>Nothing to show yet</h2>
            <p>
              Create a Dataset to start organizing your financial documents.
            </p>
            <Link to="/app/datasets/new">
              <FlowButton text="New Dataset" />
            </Link>
          </div>
        </section>
      )}
    </Page>
  );
}

function Legal({ type }: { type: "privacy" | "terms" | "how" }) {
  const content =
    type === "privacy"
      ? [
          [
            "Private document storage",
            "Uploaded PDFs are stored privately in Cloudflare R2 and are scheduled for deletion after 90 days. Generated exports use a separate, shorter retention period.",
          ],
          [
            "Document processing",
            "Local parsers and OCR are tried first. When necessary, document-derived text or page images may be processed by Z.ai using GLM-5.3-Flash.",
          ],
          [
            "Accounts and integrations",
            "Supabase provides account and application data services. Google access is requested only when you connect Google Sheets; refresh tokens remain encrypted on the server and can be disconnected.",
          ],
          [
            "Advertising and analytics",
            "Advertising is never loaded on Dataset data, review, PDF viewer or document detail pages. Privacy-aware analytics receive product events and counts, not document contents.",
          ],
          [
            "Deletion",
            "You can delete Datasets, disconnect Google and request account deletion. Retention reconciliation removes expired and orphaned objects.",
          ],
        ]
      : type === "terms"
        ? [
            [
              "Free service and fair use",
              "Bulk Invoice Data Extractor is a free, advertising-supported service. Configurable limits protect availability and processing costs. Automated abuse and attempts to bypass limits are prohibited.",
            ],
            [
              "Your documents",
              "You retain ownership of uploaded documents and confirm that you have the right to process them.",
            ],
            [
              "Accuracy",
              "Extraction can be incomplete or inaccurate. Review results before accounting, tax filing or business decisions. Bulk Invoice Data Extractor does not provide accounting or tax advice.",
            ],
            [
              "Availability",
              "Background processing and integrations may occasionally fail or be unavailable. Failed documents can be retried independently.",
            ],
          ]
        : [
            [
              "Upload",
              "Create a Dataset and upload up to 300 PDFs directly to private storage.",
            ],
            [
              "Pick fields",
              "We inspect representative layouts and recommend useful document and repeating fields.",
            ],
            [
              "Process",
              "Deterministic extraction and OCR run first; GLM handles only difficult interpretation.",
            ],
            [
              "Review and export",
              "Correct uncertain values beside their source evidence, then export CSV, XLSX, Sheets or JSON.",
            ],
          ];
  return (
    <main className="legal">
      <PublicHeader />
      <article>
        <span className="kicker">
          {type === "how" ? "The workflow" : "Legal"}
        </span>
        <h1>
          {type === "privacy"
            ? "Privacy"
            : type === "terms"
              ? "Terms"
              : "How it works"}
        </h1>
        <p className="legal-intro">
          Clear expectations for a financial document tool built around privacy,
          review and cost-aware processing.
        </p>
        {content.map(([h, p]) => (
          <section key={h}>
            <h2>{h}</h2>
            <p>{p}</p>
          </section>
        ))}
        <p className="updated">Last updated: 31 August 2026</p>
      </article>
    </main>
  );
}

function BlogRoute() {
  const { slug = "" } = useParams();
  return <BlogArticle slug={slug} />;
}

function App() {
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  return (
    <Routes>
      <Route path="/" element={<LandingV2 />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/forgot-password" element={<AuthPage mode="forgot" />} />
      <Route path="/reset-password" element={<AuthPage mode="reset" />} />
      <Route path="/privacy" element={<PolicyPage type="privacy" />} />
      <Route path="/terms" element={<PolicyPage type="terms" />} />
      <Route path="/cookie-policy" element={<PolicyPage type="cookies" />} />
      <Route
        path="/data-processing-agreement"
        element={<PolicyPage type="dpa" />}
      />
      <Route
        path="/acceptable-use"
        element={<PolicyPage type="acceptable-use" />}
      />
      <Route path="/security" element={<PolicyPage type="security" />} />
      <Route path="/how-it-works" element={<PolicyPage type="how" />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogRoute />} />
      <Route
        path="/bulk-invoice-control-center"
        element={
          <ProtectedApp>
            <AdminCMS />
          </ProtectedApp>
        }
      />
      {[
        "invoice-pdf-to-excel",
        "receipt-pdf-to-excel",
        "bank-statement-pdf-to-excel",
        "pdf-to-csv",
        "pdf-to-excel",
        "pdf-to-google-sheets",
        "bulk-pdf-data-extraction",
      ].map((path) => (
        <Route key={path} path={`/${path}`} element={<Legal type="how" />} />
      ))}
      <Route
        path="/app/*"
        element={
          <ProtectedApp>
            <AppShell>
              <Routes>
                <Route index element={<Dashboard />} />
                <Route
                  path="folders"
                  element={<SimplePage title="Folders" />}
                />
                <Route
                  path="recent"
                  element={<SimplePage title="Recent Datasets" />}
                />
                <Route path="exports" element={<Exports />} />
                <Route
                  path="settings"
                  element={<SimplePage title="Settings" />}
                />
                <Route path="admin" element={<Admin />} />
                <Route path="datasets/new" element={<NewDataset />} />
                <Route path="datasets/:id/upload" element={<UploadPage />} />
                <Route path="datasets/:id/fields" element={<FieldsPage />} />
                <Route
                  path="datasets/:id/processing"
                  element={<ProcessingPage />}
                />
                <Route path="datasets/:id/review" element={<ReviewPage />} />
                <Route path="datasets/:id" element={<DatasetPage />} />
              </Routes>
            </AppShell>
          </ProtectedApp>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
export default App;
